/**
 * Firebase Cloud Messaging (FCM) Web Push helper
 * Handles permission request, token retrieval, foreground messages, and backend registration.
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axios from 'axios';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _app = null;
let _messaging = null;

async function getMessagingInstance() {
  if (_messaging) return _messaging;
  if (!(await isSupported())) return null;
  if (!_app) {
    _app = getApps()[0] || initializeApp(firebaseConfig);
  }
  _messaging = getMessaging(_app);
  return _messaging;
}

/** Returns "default" | "granted" | "denied" | "unsupported" */
export function getPermissionStatus() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

/**
 * Request permission, get the FCM token, and register it with the backend.
 * Returns { ok, token, error } where ok=true means push is now enabled.
 */
export async function enablePush() {
  try {
    if (!(await isPushSupported())) {
      return { ok: false, error: 'Push notifications are not supported on this browser.' };
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, error: 'Permission denied.' };
    }
    const messaging = await getMessagingInstance();
    if (!messaging) return { ok: false, error: 'Messaging unavailable.' };

    // Ensure service worker is registered (CRA usually does this, but be defensive)
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      return { ok: false, error: 'Failed to retrieve FCM token.' };
    }

    // Send the token to backend along with detected timezone
    const authToken = localStorage.getItem('token');
    const tz =
      (Intl && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
    await axios.post(
      `${API}/notifications/register-fcm-token`,
      {
        token,
        device_info: { userAgent: navigator.userAgent, platform: navigator.platform, timezone: tz },
      },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );

    // Save tz into user's notification preferences (so the scheduler can use it)
    try {
      const prefsRes = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const current = prefsRes.data || {};
      delete current.user_id;
      delete current.updated_at;
      await axios.put(
        `${API}/notifications/preferences`,
        { ...current, timezone: tz },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (_) {
      // Non-fatal — user can update later from the prefs UI
    }

    localStorage.setItem('fcm_token', token);
    return { ok: true, token };
  } catch (e) {
    console.error('enablePush failed', e);
    return { ok: false, error: e.message || 'Unknown error.' };
  }
}

/**
 * Disable push: tell backend to forget this device token.
 */
export async function disablePush() {
  const token = localStorage.getItem('fcm_token');
  if (!token) return { ok: true };
  try {
    const authToken = localStorage.getItem('token');
    await axios.post(
      `${API}/notifications/unregister-fcm-token`,
      { token },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    localStorage.removeItem('fcm_token');
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

/**
 * Subscribe to foreground messages (when the app tab is open).
 * `handler` receives the payload { notification: {title, body}, data: {...} }
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
